import Component from '@glimmer/component';
import { service } from '@ember/service';
import { variation } from 'ember-launch-darkly';
import { and } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import {
  HdsDropdown,
  HdsLinkInline,
  HdsIcon,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import { TYPE_CONSUL, TYPE_VAULT } from 'common/utils/cloud-resource-types';

/**
 *
 * `CreateClusterButton` renders a button and dropdown menu which allows you to Create a Vault or Consul cluster.
 *
 *
 * ```
 * <CreateClusterButton
 *   @model={{@model}}
 * />
 * ```
 *
 * @class CreateClusterButton
 *
 */
export default class CreateClusterButton extends Component {
  /**
   *
   * `model` is an HVN object.
   * @argument model;
   * @type {Object}
   *
   */
  @service abilities;
  @service analytics;

  get consulCreateTitle() {
    if (variation('hcp-rebrand-milestone-0')) {
      return 'components.page.hvns.detail.create-dropdown.consul.title-dedicated';
    }
    return 'components.page.hvns.detail.create-dropdown.consul.title';
  }

  get vaultCreateTitle() {
    if (variation('hcp-rebrand-milestone-0')) {
      return 'components.page.hvns.detail.create-dropdown.vault.title-dedicated';
    }
    return 'components.page.hvns.detail.create-dropdown.vault.title';
  }

  get showVaultCreate() {
    let clusters = this.args?.model?.dependencies || [];

    clusters = clusters.filter((c) => c.type === TYPE_VAULT);

    return this.abilities.can('create vault', clusters, {
      networks: [this.args.model.network],
    });
  }

  get isAzure() {
    return this.args.model.network.location.region.provider === 'azure';
  }

  get showConsulCreate() {
    let clusters = this.args?.model?.dependencies || [];

    clusters = clusters.filter((c) => c.type === TYPE_CONSUL);

    return this.abilities.can('create consul', clusters, {
      networks: [this.args.model.network],
    });
  }

  get consulCreateRoute() {
    return {
      routeName: 'consul.clusters.hcp.deploy.select-method.complete-deployment',
      models: [this.args.model.network.location.region.provider, 'form'],
    };
  }

  goToCreateConsulCluster = () => {
    this.analytics.trackEvent(
      'external_ui_hashicorp_network_hvn_create_cluster_dropdown_consul_clicked',
      this.args.model.network
    );
  };

  goToCreateVaultCluster = () => {
    this.analytics.trackEvent(
      'external_ui_hashicorp_network_hvn_create_cluster_dropdown_vault_clicked',
      this.args.model.network
    );
  };

  <template>
    {{#if (and this.showConsulCreate this.showVaultCreate)}}
      <HdsDropdown
        @listPosition="bottom-right"
        class="create-cluster-button"
        as |dd|
      >
        <dd.ToggleButton
          @color="primary"
          @text={{t "components.page.hvns.detail.create-dropdown.title"}}
          data-test-create-cluster
        />
        <dd.Generic>
          <HdsLinkInline
            @models={{this.consulCreateRoute.models}}
            @route={{this.consulCreateRoute.routeName}}
            @query={{hash
              project_id=@model.network.location.projectId
              network_id=@model.network.id
            }}
            class="cluster-link"
            data-test-create-consul
            {{on "click" this.goToCreateConsulCluster}}
          >
            <HdsIcon
              @name="consul-color"
              @size="24"
              @isInline={{true}}
              class="cluster-link__icon"
            />
            <dd.Title
              @text={{t this.consulCreateTitle}}
              class="cluster-link__title"
            />
            <dd.Description
              @text={{t
                "components.page.hvns.detail.create-dropdown.consul.description"
              }}
            />
          </HdsLinkInline>
        </dd.Generic>
        <dd.Separator />
        <dd.Generic>
          <HdsLinkInline
            @query={{hash
              project_id=@model.network.location.projectId
              network_id=@model.network.id
            }}
            @route="vault.create.cluster"
            class="cluster-link"
            data-test-create-vault
            {{on "click" this.goToCreateVaultCluster}}
          >
            {{#if (variation "hcp-rebrand-milestone-0")}}
              <HdsIcon
                @name="vault-color"
                @size="24"
                @isInline={{true}}
                class="cluster-link__icon"
              />
            {{else}}
              <HdsIcon
                @name="vault"
                @size="24"
                @isInline={{true}}
                class="cluster-link__icon"
              />
            {{/if}}
            <dd.Title
              @text={{t this.vaultCreateTitle}}
              class="cluster-link__title"
            />
            <dd.Description
              @text={{t
                "components.page.hvns.detail.create-dropdown.vault.description"
              }}
            />
          </HdsLinkInline>
        </dd.Generic>
      </HdsDropdown>
    {{else if this.showConsulCreate}}
      <HdsButton
        @color="primary"
        @icon="plus"
        @iconPosition="leading"
        @models={{this.consulCreateRoute.models}}
        @query={{hash
          project_id=@model.network.location.projectId
          network_id=@model.network.id
        }}
        @route={{this.consulCreateRoute.routeName}}
        @text={{t "components.page.hvns.detail.create-consul-cluster"}}
        data-test-create-consul
      />
    {{else if this.showVaultCreate}}
      <HdsButton
        @color="primary"
        @icon="plus"
        @iconPosition="leading"
        @query={{hash
          project_id=@model.network.location.projectId
          network_id=@model.network.id
        }}
        @route="vault.create.cluster"
        @text={{t "components.page.hvns.detail.create-vault-cluster"}}
        data-test-create-vault
      />
    {{/if}}
  </template>
}
